import { useState } from "react";
import { commentsCreateComment, commentsListComments } from "../../gen";
import type {
  CommentResponse,
  RuleResponse,
  KnowledgeItemResponse,
  EvaluationResponse,
} from "../../gen/models";
import type { InPlaceComposerState } from "./InPlaceCommentComposer";
import { useToast } from "../../context/ToastContext";
import { extractListItems } from "./threadUtils";

interface UseInPlaceComposerProps {
  threadId?: string;
  activeBatchId: string;
  evaluations: EvaluationResponse[];
  setComments: React.Dispatch<React.SetStateAction<CommentResponse[]>>;
  onRequireActiveBatch?: (onReady: (batchId: string) => void) => void;
  onCommentCreated?: (tempId: string, newComment: CommentResponse) => void;
}

export function useInPlaceComposer({
  threadId,
  activeBatchId,
  evaluations,
  setComments,
  onRequireActiveBatch,
  onCommentCreated,
}: UseInPlaceComposerProps) {
  const { showToast } = useToast();
  const [inPlaceComposer, setInPlaceComposer] = useState<InPlaceComposerState | null>(null);
  const [composerText, setComposerText] = useState("");
  const [composerRuleIds, setComposerRuleIds] = useState<string[]>([]);
  const [composerKnowledgeIds, setComposerKnowledgeIds] = useState<string[]>([]);
  const [savingComposer, setSavingComposer] = useState(false);

  const [composerMentionType, setComposerMentionType] = useState<"rule" | "knowledge" | null>(null);
  const [composerMentionQuery, setComposerMentionQuery] = useState("");

  const handleMessageMouseUp = (messageId: string, threadIdForTurn?: string) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const text = selection.toString().trim();
    if (!text || text.length < 2) return;

    const range = selection.getRangeAt(0);
    void range;

    const elem = document.getElementById(`msg-text-${messageId}`);
    let startOffset = 0;
    let endOffset = text.length;

    if (elem) {
      const fullText = elem.textContent || "";
      const idx = fullText.indexOf(text);
      if (idx !== -1) {
        startOffset = idx;
        endOffset = idx + text.length;
      }
    }

    const relatedEval = evaluations.find((e) => e.message_id === messageId);

    setInPlaceComposer({
      threadId: threadIdForTurn || threadId,
      messageId,
      selectedText: text,
      start: startOffset,
      end: endOffset,
      evaluationId: relatedEval?.id,
    });
    setComposerText("");
    setComposerRuleIds([]);
    setComposerKnowledgeIds([]);
    setComposerMentionType(null);
    setComposerMentionQuery("");
  };

  const openTurnComposer = (msgId?: string, evalId?: string, threadIdForTurn?: string, initialText?: string) => {
    setInPlaceComposer({
      threadId: threadIdForTurn || threadId,
      messageId: msgId,
      selectedText: "",
      start: 0,
      end: 0,
      evaluationId: evalId,
    });
    setComposerText(initialText || "");
    setComposerRuleIds([]);
    setComposerKnowledgeIds([]);
    setComposerMentionType(null);
    setComposerMentionQuery("");
  };

  const closeComposer = () => {
    setInPlaceComposer(null);
    setComposerMentionType(null);
    setComposerMentionQuery("");
  };

  const handleComposerTextChange = (val: string) => {
    setComposerText(val);
    const ruleMatch = /@rule:([a-zA-Z0-9_\-\s]*)$/.exec(val);
    if (ruleMatch) {
      setComposerMentionType("rule");
      setComposerMentionQuery(ruleMatch[1].trim().toLowerCase());
      return;
    }
    const kMatch = /@knowledge:([a-zA-Z0-9_\-\s]*)$/.exec(val);
    if (kMatch) {
      setComposerMentionType("knowledge");
      setComposerMentionQuery(kMatch[1].trim().toLowerCase());
      return;
    }
    setComposerMentionType(null);
    setComposerMentionQuery("");
  };

  const handleSelectMentionRule = (rule: RuleResponse) => {
    if (!composerRuleIds.includes(rule.id)) {
      setComposerRuleIds((prev) => [...prev, rule.id]);
    }
    setComposerText((prev) => prev.replace(/@rule:[a-zA-Z0-9_\-\s]*$/, ""));
    setComposerMentionType(null);
    setComposerMentionQuery("");
  };

  const handleSelectMentionKnowledge = (k: KnowledgeItemResponse) => {
    if (!composerKnowledgeIds.includes(k.id)) {
      setComposerKnowledgeIds((prev) => [...prev, k.id]);
    }
    setComposerText((prev) => prev.replace(/@knowledge:[a-zA-Z0-9_\-\s]*$/, ""));
    setComposerMentionType(null);
    setComposerMentionQuery("");
  };

  const removeComposerRule = (rid: string) => {
    setComposerRuleIds((prev) => prev.filter((id) => id !== rid));
  };

  const removeComposerKnowledge = (kid: string) => {
    setComposerKnowledgeIds((prev) => prev.filter((id) => id !== kid));
  };

  const handleSaveComposerComment = async () => {
    const targetThreadId = inPlaceComposer?.threadId || threadId;
    if (!composerText.trim() || !targetThreadId || !inPlaceComposer) return;

    if (!activeBatchId) {
      if (onRequireActiveBatch) {
        onRequireActiveBatch((batchId) => {
          doSaveComment(batchId);
        });
      } else {
        showToast("An active collecting batch is required to save comments", { type: "error" });
      }
      return;
    }

    await doSaveComment(activeBatchId);
  };

  const doSaveComment = async (batchId: string) => {
    const targetThreadId = inPlaceComposer?.threadId || threadId;
    if (!composerText.trim() || !targetThreadId || !inPlaceComposer) return;

    const trimmedText = composerText.trim();
    const tempId = `temp-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
    const now = new Date().toISOString();

    const optimisticComment: CommentResponse = {
      id: tempId,
      org_id: "",
      thread_id: targetThreadId,
      message_id: inPlaceComposer.messageId,
      batch_id: batchId,
      evaluation_id: inPlaceComposer.evaluationId,
      user_id: "me",
      user_name: "You",
      evaluator_type: "human",
      selection_start: inPlaceComposer.start,
      selection_end: inPlaceComposer.end,
      selected_text: inPlaceComposer.selectedText || undefined,
      comment_text: trimmedText,
      status: "open",
      rule_ids: [...composerRuleIds],
      knowledge_item_ids: [...composerKnowledgeIds],
      created_at: now,
      updated_at: now,
    };

    // Optimistically update comments immediately so there is no delay or momentary gap
    setComments((prev) => [...prev, optimisticComment]);

    // Save current composer state in case of network failure
    const savedComposerState = { ...inPlaceComposer };
    const savedText = composerText;
    const savedRuleIds = [...composerRuleIds];
    const savedKnowledgeIds = [...composerKnowledgeIds];

    // Close composer immediately for crisp UX
    setInPlaceComposer(null);
    setComposerText("");
    setComposerRuleIds([]);
    setComposerKnowledgeIds([]);
    setComposerMentionType(null);
    setComposerMentionQuery("");
    window.getSelection()?.removeAllRanges();

    setSavingComposer(true);
    try {
      const res = await commentsCreateComment({
        thread_id: targetThreadId,
        message_id: savedComposerState.messageId,
        batch_id: batchId,
        evaluation_id: savedComposerState.evaluationId,
        selected_text: savedComposerState.selectedText || undefined,
        selection_start: savedComposerState.start,
        selection_end: savedComposerState.end,
        comment_text: trimmedText,
        rule_ids: savedRuleIds,
        knowledge_item_ids: savedKnowledgeIds,
      });

      const newComment = (res as any).data;
      if (newComment?.id) {
        setComments((prev) =>
          prev.map((c) => (c.id === tempId ? newComment : c))
        );
        onCommentCreated?.(tempId, newComment);
      } else {
        const commsRes = await commentsListComments({ thread_id: targetThreadId, limit: 200 });
        setComments(extractListItems<CommentResponse>(commsRes));
      }

      showToast(savedComposerState.selectedText ? "Comment saved" : "Note saved", { type: "success" });
    } catch (err: any) {
      // Revert optimistic comment on network failure
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      // Reopen composer so user does not lose draft
      setInPlaceComposer(savedComposerState);
      setComposerText(savedText);
      setComposerRuleIds(savedRuleIds);
      setComposerKnowledgeIds(savedKnowledgeIds);
      showToast("Failed to save: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setSavingComposer(false);
    }
  };

  const handleAddAlteredNote = async (
    messageId: string,
    originalMessage: string,
    actualMessage: string,
    evalId?: string
  ) => {
    const noteText = `Generated completion: ${originalMessage.trim()}\n\nActual human corrected completion: ${actualMessage.trim()}`;

    const doSave = async (batchId: string) => {
      if (!threadId) return;
      const tempId = `temp-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
      const now = new Date().toISOString();

      const optimisticComment: CommentResponse = {
        id: tempId,
        org_id: "",
        thread_id: threadId,
        message_id: messageId,
        batch_id: batchId,
        evaluation_id: evalId,
        user_id: "me",
        user_name: "You",
        evaluator_type: "human",
        comment_text: noteText,
        status: "open",
        rule_ids: [],
        knowledge_item_ids: [],
        created_at: now,
        updated_at: now,
      };

      setComments((prev) => [...prev, optimisticComment]);

      try {
        const res = await commentsCreateComment({
          thread_id: threadId,
          message_id: messageId,
          batch_id: batchId,
          evaluation_id: evalId,
          comment_text: noteText,
          rule_ids: [],
          knowledge_item_ids: [],
        });

        const newComment = (res as any)?.data;
        if (newComment?.id) {
          setComments((prev) =>
            prev.map((c) => (c.id === tempId ? newComment : c))
          );
          onCommentCreated?.(tempId, newComment);
        } else {
          const commsRes = await commentsListComments({ thread_id: threadId, limit: 200 });
          setComments(extractListItems<CommentResponse>(commsRes));
        }

        showToast("Note added to batch", { type: "success" });
      } catch (err: any) {
        setComments((prev) => prev.filter((c) => c.id !== tempId));
        showToast("Failed to save note: " + (err?.message || "Unknown error"), { type: "error" });
      }
    };

    if (!activeBatchId) {
      if (onRequireActiveBatch) {
        onRequireActiveBatch((batchId) => {
          doSave(batchId);
        });
      } else {
        showToast("An active collecting batch is required to save notes", { type: "error" });
      }
      return;
    }

    await doSave(activeBatchId);
  };

  return {
    inPlaceComposer,
    setInPlaceComposer,
    composerText,
    composerRuleIds,
    composerKnowledgeIds,
    savingComposer,
    composerMentionType,
    composerMentionQuery,
    openTurnComposer,
    closeComposer,
    handleMessageMouseUp,
    handleComposerTextChange,
    handleSelectMentionRule,
    handleSelectMentionKnowledge,
    removeComposerRule,
    removeComposerKnowledge,
    handleSaveComposerComment,
    handleAddAlteredNote,
  };
}
